import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../services/api/axios";

const BASE_URL = "/v1/admin/personal/campaigns";

// list campaigns
const fetchCampaigns = async () => {
  const response = await api.get(BASE_URL);
  return response.data;
};

export const useCampaigns = () => {
  return useQuery({
    queryKey: ["campaigns"],
    queryFn: fetchCampaigns,
    refetchOnWindowFocus: false,
  });
};

// single campaign
const fetchCampaign = async (uuid) => {
  const { data } = await api.get(`${BASE_URL}/${uuid}`);
  return data;
};

export const useCampaign = (uuid) => {
  return useQuery({
    queryKey: ["campaign", uuid],
    queryFn: () => fetchCampaign(uuid),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  });
};

// create campaign
const createCampaign = async (payload) => {
  const { data } = await api.post(BASE_URL, payload);
  return data;
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
};

// update campaign
const updateCampaign = async (uuid, payload) => {
  const { data } = await api.patch(`${BASE_URL}/${uuid}`, payload);
  return data;
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, payload }) => updateCampaign(uuid, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign", variables.uuid] });
    },
  });
};

// eligible users for a campaign
const fetchEligibleUsers = async (uuid) => {
  const { data } = await api.get(`${BASE_URL}/${uuid}/eligible-users`);
  return data;
};

export const useEligibleUsers = (uuid) => {
  return useQuery({
    queryKey: ["campaign-eligible-users", uuid],
    queryFn: () => fetchEligibleUsers(uuid),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  });
};

// redemptions for a single campaign
const fetchCampaignRedemptions = async (uuid) => {
  const { data } = await api.get(`${BASE_URL}/${uuid}/redemptions`);
  return data;
};

export const useCampaignRedemptions = (uuid) => {
  return useQuery({
    queryKey: ["campaign-redemptions", uuid],
    queryFn: () => fetchCampaignRedemptions(uuid),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  });
};

// global redemptions view (all campaigns)
const fetchAllRedemptions = async () => {
  const { data } = await api.get(`${BASE_URL}/redemptions`);
  return data;
};

export const useAllRedemptions = () => {
  return useQuery({
    queryKey: ["all-campaign-redemptions"],
    queryFn: fetchAllRedemptions,
    refetchOnWindowFocus: false,
  });
};

// pending manual-review task submissions for a campaign
const fetchPendingReviews = async (uuid) => {
  const { data } = await api.get(`${BASE_URL}/${uuid}/pending-reviews`);
  return data;
};

export const usePendingReviews = (uuid) => {
  return useQuery({
    queryKey: ["campaign-pending-reviews", uuid],
    queryFn: () => fetchPendingReviews(uuid),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  });
};

// approve a manual-review task submission
const approveTask = async (uuid, taskUuid, progressUuid) => {
  const { data } = await api.post(
    `${BASE_URL}/${uuid}/tasks/${taskUuid}/reviews/${progressUuid}/approve`
  );
  return data;
};

export const useApproveTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, taskUuid, progressUuid }) =>
      approveTask(uuid, taskUuid, progressUuid),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-pending-reviews", variables.uuid],
      });
      queryClient.invalidateQueries({
        queryKey: ["campaign-eligible-users", variables.uuid],
      });
    },
  });
};

// reject a manual-review task submission (payload: { reason: string })
const rejectTask = async (uuid, taskUuid, progressUuid, payload) => {
  const { data } = await api.post(
    `${BASE_URL}/${uuid}/tasks/${taskUuid}/reviews/${progressUuid}/reject`,
    payload
  );
  return data;
};

export const useRejectTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, taskUuid, progressUuid, payload }) =>
      rejectTask(uuid, taskUuid, progressUuid, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-pending-reviews", variables.uuid],
      });
    },
  });
};

// pending final-redemption approval requests (Campaign.requiresApprovalToRedeem)
// - distinct from pending-reviews above, which is per-task.
const fetchPendingRedemptions = async (uuid) => {
  const { data } = await api.get(`${BASE_URL}/${uuid}/pending-redemptions`);
  return data;
};

export const usePendingRedemptions = (uuid) => {
  return useQuery({
    queryKey: ["campaign-pending-redemptions", uuid],
    queryFn: () => fetchPendingRedemptions(uuid),
    enabled: !!uuid,
    refetchOnWindowFocus: false,
  });
};

const approveRedemption = async (uuid, progressUuid) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/redemptions/${progressUuid}/approve`);
  return data;
};

export const useApproveRedemption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, progressUuid }) => approveRedemption(uuid, progressUuid),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-pending-redemptions", variables.uuid] });
      queryClient.invalidateQueries({ queryKey: ["campaign-redemptions", variables.uuid] });
    },
  });
};

const rejectRedemption = async (uuid, progressUuid, payload) => {
  const { data } = await api.post(`${BASE_URL}/${uuid}/redemptions/${progressUuid}/reject`, payload);
  return data;
};

export const useRejectRedemption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, progressUuid, payload }) => rejectRedemption(uuid, progressUuid, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-pending-redemptions", variables.uuid] });
    },
  });
};

// The switch that pauses every consumer campaign at once (coupon campaigns and banners,
// product update emails and the daily rate pushes; payout, deposit and KYC alerts are not affected).
export const useAllCampaignsPaused = () => {
  return useQuery({
    queryKey: ["campaigns-all-paused"],
    queryFn: async () => {
      const { data } = await api.get(`${BASE_URL}/pause`);
      return data?.data?.paused === true;
    },
    refetchOnWindowFocus: false,
  });
};

export const useSetAllCampaignsPaused = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paused) => {
      const { data } = await api.put(`${BASE_URL}/pause`, { paused });
      return data?.data?.paused === true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns-all-paused"] });
    },
  });
};
